/**
 * La génération d'un avatar par IA, sa part pure : l'état de la feuille, la ligne « où en est
 * l'élève de ses générations du mois », et ce que disent l'envoi refusé et chaque statut du serveur.
 *
 * Les phrases sont celles de `GenerateAIAvatarModal.js`, mot pour mot : elles passaient dans des
 * boîtes, elles passent dans la feuille (un envoi refusé) ou dans l'avis de la page (l'issue d'une
 * génération, la feuille refermée). Testé seul (`avatarAIGeneration.test.ts`).
 */
import { type StudentAccountNotice } from "../../../../../../design-system/pages/StudentAccountPage/StudentAccountPage"
import { type UIAvatarGenerationInfo } from "../../../../../../design-system/templates/AccountAvatarAIModal/AccountAvatarAIModal"
import { studentRefusalText } from "../../../hooks/studentRefusal"

/** Le `formId` de l'appareil photo de l'app pour cette feuille — celui de l'ancienne modale. */
export const AVATAR_CAMERA_FORM_ID = "avatar_generation"
/** Le statut est relu toutes les 3 s, 90 s au plus (30 relectures), comme avant. */
export const STATUS_POLL_EVERY_MS = 3000
export const STATUS_POLL_TIMEOUT_MS = 90000

// ── La feuille ─────────────────────────────────────────────────────────────────────────────

export type AvatarAIState = {
    open: boolean
    /** La photo choisie (une adresse locale). */
    photoUri: string | null
    /** La photo part. */
    sending: boolean
    /** Un envoi refusé, dit dans la feuille. */
    error: string | null
    /** Les générations qui restent ce mois — `null` tant que le serveur ne l'a pas dit. */
    remaining: number | null
    /** L'issue d'une génération, dite sur la page. */
    notice: StudentAccountNotice | null
}

export type AvatarAIAction =
    | { type: "OPEN" }
    | { type: "CLOSE" }
    | { type: "PHOTO"; uri: string }
    | { type: "SEND" }
    | { type: "SENT"; remaining?: number | null }
    | { type: "REFUSED"; message: string }
    | { type: "REMAINING"; remaining?: number | null }
    | { type: "NOTICE"; notice: StudentAccountNotice }
    | { type: "DISMISS_NOTICE" }

export const initialAvatarAIState = (remaining: number | null = null): AvatarAIState => ({
    open: false,
    photoUri: null,
    sending: false,
    error: null,
    remaining,
    notice: null,
})

/**
 * Le compte du mois, quand le serveur le dit. Inchangé, le même état : la relève le relit toutes les
 * 3 s, et un état neuf à chaque relecture redessinerait l'écran pour rien.
 */
const withRemaining = (state: AvatarAIState, remaining?: number | null): AvatarAIState =>
    typeof remaining === "number" && remaining !== state.remaining ? { ...state, remaining } : state

export function avatarAIReducer(state: AvatarAIState, action: AvatarAIAction): AvatarAIState {
    switch (action.type) {
        case "OPEN":
            // L'ancienne modale repartait sans photo à chaque ouverture.
            return { ...state, open: true, photoUri: null, error: null }
        case "CLOSE":
            // Pendant l'envoi la feuille ne se ferme pas : la réponse la refermera.
            return state.sending ? state : { ...state, open: false }
        case "PHOTO":
            return { ...state, photoUri: action.uri, error: null }
        case "SEND":
            return { ...state, sending: true, error: null }
        case "SENT":
            return withRemaining({ ...state, sending: false, open: false }, action.remaining)
        case "REFUSED":
            return { ...state, sending: false, error: action.message }
        case "REMAINING":
            return withRemaining(state, action.remaining)
        case "NOTICE":
            return { ...state, notice: action.notice }
        case "DISMISS_NOTICE":
            return state.notice == null ? state : { ...state, notice: null }
        default:
            return state
    }
}

/** La ligne du pied de l'ancienne modale, et si elle bloque le bouton. */
export function avatarGenerationInfo(generating: boolean, remaining: number | null): UIAvatarGenerationInfo {
    if (generating)
        return { text: "Génération en cours - Attends la fin avant d'en lancer une nouvelle", blocking: true }
    if (remaining == null) return { text: "Limite de 3 avatars par mois", blocking: false }
    if (remaining === 0) return { text: "Limite atteinte - Réessaye le mois prochain", blocking: true }
    return {
        text: `${remaining} avatar${remaining > 1 ? "s" : ""} restant${remaining > 1 ? "s" : ""} ce mois`,
        blocking: false,
    }
}

// ── L'envoi ────────────────────────────────────────────────────────────────────────────────

/** L'échec de l'envoi, tel que le réducteur d'api le range (`{ ...response.data, status }`). */
export type AvatarAIFailure = { status?: number | null; message?: string | null; retry_after?: unknown } | null

/** Le repli d'une génération refusée ou en échec, et d'une phrase du serveur qu'on ne peut pas dire. */
export const GENERATION_FAILED = "Une erreur est survenue lors de la génération. Réessaye."

export const GENERATION_STARTED: StudentAccountNotice = {
    message: "Génération lancée : ton avatar IA sera prêt dans quelques instants.",
    tone: "info",
}

/** Ce que dit un envoi refusé : la limite du mois (429), l'accès refusé (403), ou la phrase du serveur. */
export function generationRefusal(failure: AvatarAIFailure): string {
    if (failure?.status === 429) {
        return failure.retry_after != null
            ? "Tu as atteint la limite de 3 avatars par mois. Réessaye le mois prochain."
            : "Tu as atteint la limite de 3 avatars par mois."
    }
    if (failure?.status === 403) return "Tu n'es pas autorisé à générer un avatar pour cet étudiant."
    // La phrase du serveur, au tutoiement et en français ; sinon le repli.
    return studentRefusalText(typeof failure?.message === "string" ? failure.message : null, GENERATION_FAILED)
}

// ── Le statut ──────────────────────────────────────────────────────────────────────────────

/** La réponse de `profile-image-status`. */
export type ApiProfileImageStatus = {
    status?: string | null
    message?: string | null
    validation_reason?: string | null
    generations_remaining?: number | null
}

const VALIDATION_MESSAGES: Record<string, string> = {
    not_a_face: "Merci de télécharger une vraie photo de ton visage.",
    multiple_faces: "Merci de télécharger une photo avec une seule personne.",
    nsfw: "Cette image n'est pas appropriée.",
    celebrity_risk: "Nous ne pouvons pas créer d'avatars de personnalités publiques.",
    poor_quality: "Merci de télécharger une photo plus claire et mieux éclairée.",
}

/**
 * Ce que fait l'écran d'un statut lu :
 *  - `done` : l'avatar est prêt, relire le compte (la relève s'arrête, l'avatar travaille encore) ;
 *  - `stop` : la relève s'arrête, avec un avis quand une génération était en cours ;
 *  - `follow` : la génération tourne (après une réouverture de l'écran : la relève reprend).
 * `notice` n'est dit que si l'élève attendait une génération (`generating`), comme avant.
 */
export type StatusOutcome =
    | { kind: "done"; notice: StudentAccountNotice | null }
    | { kind: "stop"; notice: StudentAccountNotice | null }
    | { kind: "follow" }

export function statusOutcome(data: ApiProfileImageStatus | null | undefined, generating: boolean): StatusOutcome {
    const tell = (notice: StudentAccountNotice) => (generating ? notice : null)
    switch (data?.status) {
        case "completed":
            return {
                kind: "done",
                notice: tell({
                    message: "Avatar prêt ! Ton nouvel avatar IA a été généré avec succès.",
                    tone: "success",
                }),
            }
        case "validation_failed": {
            const reason = data.validation_reason ?? ""
            const message =
                VALIDATION_MESSAGES[reason] ??
                studentRefusalText(data.message, "Merci de télécharger une photo valide.")
            return { kind: "stop", notice: tell({ message: `Image non valide : ${message}`, tone: "danger" }) }
        }
        case "failed":
            return {
                kind: "stop",
                notice: tell({ message: studentRefusalText(data.message, GENERATION_FAILED), tone: "danger" }),
            }
        case "processing":
            return { kind: "follow" }
        case "idle":
            return { kind: "stop", notice: null }
        default:
            return {
                kind: "stop",
                notice: tell({ message: "Une erreur inattendue est survenue. Réessaye.", tone: "danger" }),
            }
    }
}

/** Une relecture du statut qui échoue. */
export const STATUS_READ_FAILED: StudentAccountNotice = {
    message: "Une erreur s'est produite. Réessaye.",
    tone: "danger",
}

/** La relève a duré 90 s sans réponse finale. */
export const STATUS_TIMED_OUT: StudentAccountNotice = {
    message: "La génération prend plus de temps que prévu. Ton avatar apparaîtra bientôt.",
    tone: "info",
}
